import React, { memo } from 'react';
import {
  StorySummaryChatPanel,
  VisualStyleChatPanel,
  CharactersChatPanel,
  PropsChatPanel,
  ScenesChatPanel,
  ShotsChatPanel
} from '../chatPanels';
import { IProjectAsset, AssetType } from '@packages/global';

interface ChatPanelRendererProps {
  currentStep: number;
  projectId: string | undefined;
  scriptId: string | undefined;
  currentStepData: any;
  onSimpleApply: (data: any) => Promise<void>;
  onCharactersApply: (action: any) => Promise<void>;
  onPropsApply: (action: any) => Promise<void>;
  onScenesApply: (action: any) => Promise<void>;
  onShotsApply: (action: any) => Promise<void>;
  /** 剧集主体列表（ProjectAsset），用于 Step 3/4/5 chat panel 的 currentData */
  episodeAssets?: IProjectAsset[];
}

const ChatPanelRenderer = memo(function ChatPanelRenderer({
  currentStep, projectId, scriptId, currentStepData,
  onSimpleApply, onCharactersApply, onPropsApply, onScenesApply, onShotsApply,
  episodeAssets = [],
}: ChatPanelRendererProps) {
  if (!scriptId || !projectId) return null;

  switch (currentStep) {
    case 1:
      return (
        <StorySummaryChatPanel
          key={`step1-${scriptId}`}
          projectId={projectId}
          episodeScriptId={scriptId}
          currentData={currentStepData}
          onApply={onSimpleApply}
        />
      );
    case 2:
      return (
        <VisualStyleChatPanel
          key={`step2-${scriptId}`}
          projectId={projectId}
          episodeScriptId={scriptId}
          currentData={currentStepData}
          onApply={onSimpleApply}
        />
      );
    case 3: {
      // 从 episodeAssets 构造 currentData，包含 id 供 LLM 回传 entityId
      const characters = episodeAssets
        .filter(a => a.assetType === AssetType.CHARACTER)
        .map(a => ({ id: a.id, name: a.name, prompt: a.prompt }));
      return (
        <CharactersChatPanel
          key={`step3-${scriptId}`}
          projectId={projectId}
          episodeScriptId={scriptId}
          currentData={characters.length > 0 ? { characters: characters as any } : currentStepData}
          onApply={onCharactersApply}
        />
      );
    }
    case 4: {
      const props = episodeAssets
        .filter(a => a.assetType === AssetType.PROP)
        .map(a => ({ id: a.id, name: a.name, prompt: a.prompt }));
      return (
        <PropsChatPanel
          key={`step4-${scriptId}`}
          projectId={projectId}
          episodeScriptId={scriptId}
          currentData={props.length > 0 ? { props: props as any } : currentStepData}
          onApply={onPropsApply}
        />
      );
    }
    case 5: {
      const scenes = episodeAssets
        .filter(a => a.assetType === AssetType.SCENE)
        .map(a => ({ id: a.id, name: a.name, prompt: a.prompt }));
      return (
        <ScenesChatPanel
          key={`step5-${scriptId}`}
          projectId={projectId}
          episodeScriptId={scriptId}
          currentData={scenes.length > 0 ? { scenes: scenes as any } : currentStepData}
          onApply={onScenesApply}
        />
      );
    }
    case 6:
      return (
        <ShotsChatPanel
          key={`step6-${scriptId}`}
          projectId={projectId}
          episodeScriptId={scriptId}
          currentData={currentStepData}
          onApply={onShotsApply}
        />
      );
    default:
      return null;
  }
});

export default ChatPanelRenderer;
